import groupBy from 'lodash/groupBy'

import { employeeUser } from 'modules/core/store/collections/users'

import employeesCollection from 'modules/employees/store/collections/employees'
import Holidays from 'modules/shift_management/store/internal/collections/Holidays'
import Leaves from 'modules/shift_management/store/internal/collections/Leaves'
import PlanningVersions from 'modules/shift_management/store/internal/collections/PlanningVersions'
import RestDays from 'modules/shift_management/store/internal/collections/RestDays'
import Shifts from 'modules/shift_management/store/internal/collections/Shifts'
import { PlanningVersionPayload } from 'modules/shift_management/store/internal/models/PlanningVersion'
import Shift, {
  NO_WORK_AREA,
  ShiftPayload,
} from 'modules/shift_management/store/internal/models/Shift'
import ShiftPermissions from 'modules/shift_management/store/internal/models/ShiftPermissions'
import ShiftsRow, { ShiftsRowType } from 'modules/shift_management/store/internal/models/ShiftsRow'
import type CompanyHoliday from 'modules/timeoff/store/models/CompanyHoliday'

import { EmployeePermissions } from './types'

export interface WorkAreaPayload {
  id: number
  name: string
  location_id: number
  location_name: string
  archived: boolean
}

const buildWorkAreaParamsFromResources = (
  workAreas: WorkAreaPayload[] | undefined,
  employees: EmployeePermissions[],
  planningVersions: PlanningVersionPayload[] | undefined,
  shifts: ShiftPayload[] | undefined,
  companyHolidays: CompanyHoliday[]
): ShiftsRow[] => {
  if (!workAreas) {
    return []
  }

  const groupedShiftsByWorkArea = groupBy(shifts, (shift) => shift.work_area_id)
  const shiftsNoWorkArea = groupedShiftsByWorkArea['null']
  const groupedShiftsByLocation = groupBy(shiftsNoWorkArea, (shift) => {
    const employee = employeesCollection.find({ id: shift.employee_id })
    return shift.location_id || employee?.attributes.location_id
  })

  return workAreas.map((workArea) => {
    const workAreaId = workArea.id || NO_WORK_AREA
    const shiftsFromWorkArea: ShiftPayload[] = workArea.id
      ? groupedShiftsByWorkArea[workArea.id] || []
      : groupedShiftsByLocation[workArea.location_id] || []

    // Send all the employees for each row even if they don't have shifts to make the shifts addition easier

    const canCreateShifts = employees.some((employee) => {
      return employee.permissions.create
    })

    // We won't handle leaves in the work area view
    const permissions = {
      create: canCreateShifts,
      create_leave: false,
      approve_leave: false,
      delete_leave: false,
    }

    return new ShiftsRow({
      id: workArea.id,
      type: ShiftsRowType.workArea,
      title: workArea.name,
      subtitle: workArea.location_name,
      key: `${workArea.location_id}-${workAreaId}`,
      permissions: ShiftPermissions.buildFromRequest(permissions),
      shifts: new Shifts(
        shiftsFromWorkArea.map((shift) => {
          const employee = employeesCollection.find({ id: shift.employee_id })
          const user = employee && employeeUser(employee)
          return Shift.buildFromRequest({
            id: shift.id,
            employee_id: shift.employee_id,
            name: shift.name,
            start_at: shift.start_at,
            end_at: shift.end_at,
            state: shift.state,
            location_id: shift.location_id,
            work_area_id: shift.work_area_id,
            notes: shift.notes,
            extra_hours: shift.extra_hours,
            is_overnight_by_calendar_day: shift.is_overnight_by_calendar_day,
            color: shift.color,
            default_shift_id: shift.default_shift_id,
            default_shift_title: shift.default_shift_title,
            template_week_id: shift.template_week_id,
            unassigned_shift_id: shift.unassigned_shift_id,
            creation_mode: shift.creation_mode,
            conflict: shift.conflict,
            timezone: shift.timezone,
            employee_full_name: user?.fullName() || '',
            employee_avatar: user?.avatar || '',
            employee_location_id: employee?.attributes.location_id || null,
          })
        })
      ),
      leaves: Leaves.buildFromProps([]),
      holidays: Holidays.buildFromProps(companyHolidays),
      restDays: RestDays.build([]),
      planningVersions: PlanningVersions.buildFromProps(planningVersions || []),
      urlParams: workArea.id ? undefined : { id: workArea.location_id },
    })
  })
}

export default buildWorkAreaParamsFromResources
